import * as React from 'react';

/**
 * Recharts tooltip content that shows a category breakdown AND the total it is
 * supposed to add up to.
 *
 * Recharts' stock tooltip lists the active series and stops there, which is the
 * failure this component exists to fix: a stacked bar is read as a total, the
 * tooltip lists parts, and nothing on screen ever asserts that the parts are
 * the whole. Where they are not — a category excluded from the chart, a
 * rounding gap, a series the server could not classify — this says so instead
 * of letting the reader assume.
 *
 * Three modes, chosen by what the caller can honestly claim:
 *
 *  - `total` omitted → the total is the sum of the parts. Shown as "Total".
 *  - `total` supplied → the caller knows the true plotted total independently.
 *    If it disagrees with the sum of the parts by more than `tolerance`, the
 *    difference is named as a residual rather than hidden.
 *  - `summable={false}` → no total is shown at all, and the reason is. This is
 *    the mixed-currency case: AED and BHD (3-decimal) cannot be added, and a
 *    combined figure would be a fabricated number, not a rounded one.
 */

export interface BreakdownTooltipEntry {
  name: string;
  value: number;
  color: string;
  formatValue?: (value: number) => string;
}

export interface BreakdownTooltipProps {
  /** Injected by Recharts. */
  active?: boolean;
  /** Injected by Recharts. */
  payload?: readonly {
    name?: string;
    value?: unknown;
    color?: string;
    /** Preserve metric-defined precision when additive measures differ. */
    formatValue?: (value: number) => string;
    dataKey?: string | number;
    /** The whole data row the hovered point came from. */
    payload?: Record<string, unknown>;
  }[];
  /** Injected by Recharts. */
  label?: string | number;

  /** Format a series value for display. */
  formatValue: (n: number) => string;
  /** Format the hovered bucket's label. Defaults to the raw label. */
  formatLabel?: (label: string) => string;
  /**
   * The true plotted total for this bucket, when the caller knows it
   * independently of the series shown. Leave undefined to use the sum.
   */
  total?: number | null;
  /**
   * Field on the hovered data row holding the independently-computed total.
   * Preferred over `total` for per-bucket totals — Recharts hands the whole
   * row to the tooltip, so the total travels with the point instead of having
   * to be looked up by label. Ignored when `total` is given.
   */
  totalKey?: string;
  /**
   * Set false when the parts cannot legitimately be added — the mixed-currency
   * case. `notSummableReason` is then required reading for the user.
   */
  summable?: boolean;
  notSummableReason?: string;
  /** Absolute tolerance below which a total/sum gap is not worth naming. */
  tolerance?: number;
  /** Hide series whose value is 0 for this bucket. Default true. */
  hideZeroSeries?: boolean;
}

export function BreakdownTooltip({
  active,
  payload,
  label,
  formatValue,
  formatLabel,
  total,
  totalKey,
  summable = true,
  notSummableReason,
  tolerance = 0.5,
  hideZeroSeries = true,
}: BreakdownTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;

  const entries: BreakdownTooltipEntry[] = [];
  for (const p of payload) {
    const raw = typeof p.value === 'number' ? p.value : Number(p.value);
    if (!Number.isFinite(raw)) continue;
    if (hideZeroSeries && raw === 0) continue;
    entries.push({
      name: p.name ?? String(p.dataKey ?? ''),
      value: raw,
      color: p.color ?? 'var(--text-muted)',
      formatValue: p.formatValue,
    });
  }

  const sum = entries.reduce((acc, e) => acc + e.value, 0);

  // Resolve the independent total BEFORE deciding whether there is anything to
  // show. The case that matters is a bucket with a real total and all-zero
  // parts — stale or unwritten breakdown columns against a live count — which
  // is precisely the disagreement this component exists to surface. Returning
  // early on "no nonzero parts" would hide it behind an empty tooltip, making
  // the loudest possible failure the quietest thing on screen.
  let resolvedTotal = total ?? null;
  if (resolvedTotal == null && totalKey) {
    const raw = payload[0]?.payload?.[totalKey];
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (Number.isFinite(n)) resolvedTotal = n;
  }

  const residual = resolvedTotal == null ? 0 : resolvedTotal - sum;
  const hasResidual = summable && resolvedTotal != null && Math.abs(residual) > tolerance;

  // Nothing to say only when there are no parts AND no total worth reporting.
  if (entries.length === 0 && !hasResidual && !(summable && resolvedTotal != null)) return null;

  return (
    <div
      // recharts adds `role="status"` + `aria-live` only on its own
      // DefaultTooltipContent, so custom content silently loses it. Without
      // these, a screen-reader user who tabs into an `accessibilityLayer` chart
      // and arrows between buckets gets a tooltip that updates visually and
      // announces nothing — the keyboard reachability would be real and the
      // information still unreachable. `polite` rather than `assertive`:
      // arrowing through buckets fires continuously and should not interrupt.
      role="status"
      aria-live="polite"
      className="rounded-lg border border-border bg-bg-surface px-3 py-2 text-caption shadow-md"
      style={{ color: 'var(--text-primary)' }}
    >
      <div className="mb-1.5 font-medium text-text-primary">
        {formatLabel && typeof label === 'string' ? formatLabel(label) : String(label ?? '')}
      </div>
      <table className="w-full border-collapse">
        <tbody>
          {entries.map((e) => (
            <tr key={e.name}>
              <td className="py-0.5 pr-3 align-middle">
                <span className="inline-flex items-center gap-1.5">
                  <span
                    aria-hidden
                    className="inline-block h-2 w-2 shrink-0 rounded-sm"
                    style={{ backgroundColor: e.color }}
                  />
                  <span className="text-text-secondary">{e.name}</span>
                </span>
              </td>
              <td className="py-0.5 text-right font-mono tabular-nums text-text-primary">
                {(e.formatValue ?? formatValue)(e.value)}
              </td>
            </tr>
          ))}
          {summable ? (
            <tr className="border-t border-border">
              <td className="pr-3 pt-1.5 font-medium text-text-primary">Total</td>
              <td className="pt-1.5 text-right font-mono font-medium tabular-nums text-text-primary">
                {formatValue(resolvedTotal ?? sum)}
              </td>
            </tr>
          ) : null}
          {hasResidual ? (
            <tr>
              <td className="pr-3 text-text-secondary">
                {entries.length === 0
                  ? 'Unaccounted for'
                  : residual < 0
                    ? 'Over the total by'
                    : 'Not shown above'}
              </td>
              <td className="text-right font-mono tabular-nums text-text-secondary">
                {formatValue(residual < 0 ? -residual : residual)}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
      {hasResidual ? (
        <p className="mt-1.5 max-w-[16rem] text-caption" style={{ color: 'var(--warning-text)' }}>
          {entries.length === 0
            ? 'No category breakdown was recorded for this point, only a total.'
            : residual < 0
              ? 'The categories above add up to more than the whole bar.'
              : 'The categories above do not account for the whole bar.'}
        </p>
      ) : null}
      {!summable ? (
        <p className="mt-1.5 max-w-[16rem] text-caption text-text-muted">
          {notSummableReason ?? 'These values are not in a single unit and are not added.'}
        </p>
      ) : null}
    </div>
  );
}
